import { Link, Typography, Button, ButtonGroup, Form, Box } from '@ace-clublabs/shared-component-library';
import React from 'react';
import { useGlobalState } from '../shared/state';
import { AppFlow } from '@lib/types';
import { getTermsUrl } from '../utils/urlHelper';
import dollarCircle from '../assets/dollar-circle.png';

interface Props {
  onClickNext: (values: any) => void;
  onCancel: () => void;
}

const getHeaderText = (appFlow: AppFlow, isStandaloneAutoPay: boolean, showOptedDiscountInfo: boolean) => {
  if (appFlow === AppFlow.AUTOPAY || showOptedDiscountInfo) {
    return 'Confirm your Auto Pay enrollment';
  }
  if (isStandaloneAutoPay) {
    return 'You have no payment due. Would you like to set up Auto Pay for future payments?';
  }
  return 'Would you like to enroll in Auto Pay?';
};

const AutopayEnrollmentForm: React.FC<Props> = ({ onClickNext, onCancel }) => {
  const [autoPay] = useGlobalState('autoPay');
  const [optedDiscount] = useGlobalState('optedDiscount');
  const [amountDue] = useGlobalState('amountDue');
  const [appFlow] = useGlobalState('appFlow');

  const { discount, eligible } = autoPay || {};

  const isStandaloneAutoPay = eligible === true && !amountDue;
  const showOptedDiscountInfo = !!discount && optedDiscount;
  const headerText = getHeaderText(appFlow, isStandaloneAutoPay, showOptedDiscountInfo);

  const renderAutoPayMessageBody = () => {
    const discountMessage = amountDue ? (
      <Typography variant="b1Regular" sx={{ paddingLeft: '0.5rem' }}>
        <span style={{ marginRight: '0.5rem' }}>•</span> Save<strong> ${discount?.toFixed(2)} </strong>today by
        enrolling in Auto Pay
      </Typography>
    ) : (
      <Typography variant="b1Regular" sx={{ paddingLeft: '0.5rem' }}>
        <span style={{ marginRight: '0.5rem' }}>•</span> Save<strong> ${discount?.toFixed(2)}</strong> on your next
        billing cycle by enrolling in Auto Pay
      </Typography>
    );
    const discountCard = discount ? <div id="discount-card">{discountMessage}</div> : <></>;

    return (
      <>
        <Typography variant="b1Regular" id="autopay-enrollment-1" sx={{ margin: '.5rem 0 1rem 0' }}>
          Never miss a payment with Auto Pay.
        </Typography>
        <Box sx={{ backgroundColor: '#FAF8F4', margin: '1rem 0 1.5rem 0', padding: '1rem', borderRadius: '6px' }} id='autopay-enrollment-container'>
          <Typography
            variant="b1Medium"
            sx={{ color: '#0C7A93', marginBottom: '0.25rem', display: 'flex', alignItems: 'center' }}
          >
            <img src={dollarCircle} alt="" style={{ width: '20px', height: '20px', marginRight: '0.5rem' }} />
            Auto Pay benefits
          </Typography>
          {discountCard}
          <Typography variant="b1Regular" sx={{ paddingLeft: '0.5rem' }}>
            <span style={{ marginRight: '0.5rem' }}>•</span> Enjoy automatic membership renewal
          </Typography>
          <Typography variant="b1Regular" sx={{ paddingLeft: '0.5rem' }}>
            <span style={{ marginRight: '0.5rem' }}>•</span> Keep your membership active with payments that are always
            on time
          </Typography>
        </Box>
      </>
    );
  };

  const generateFormButtons = () => {
    return (
      <ButtonGroup>
        <Button
          id="enroll-continue-btn"
          variant="contained"
          onClick={() => onClickNext({ selections: true })}
          className="btn-large"
          label="Enroll and continue"
          disabled={false}
          fullWidth
        ></Button>
        {appFlow === AppFlow.AUTOPAY || showOptedDiscountInfo ? (
          <></>
        ) : isStandaloneAutoPay ? (
          <Button id="cancel-btn" variant="outlined" onClick={onCancel} label="Cancel" disabled={false}></Button>
        ) : (
          <Button
            id="skip-and-paym-btn"
            variant="outlined"
            onClick={() => onClickNext({ selections: false })}
            label="Skip and pay"
            disabled={false}
          ></Button>
        )}
      </ButtonGroup>
    );
  };

  const renderAutoPayFooter = () => {
    return (
      <div className="autopay-flow-legal">
        <Typography variant="b2Regular" id="autopay-flow-legal" sx={{ color: '#6C7175' }}>
          By clicking Enroll and continue, I hereby enroll in Auto Pay and have read and agree to the{' '}
          <Link
            className="secondary"
            id="terms-and-conditions-link"
            variant="b2Regular"
            target="_blank"
            href={getTermsUrl()}
          >
            Terms & Conditions.
          </Link>
        </Typography>
      </div>
    );
  };

  return (
    <>
      <div className="autopay-enrollment-form">
        <Typography variant="h5" component="h1" id="header" sx={{ fontWeight: '500', marginTop: '20px' }}>
          {headerText}
        </Typography>
        {renderAutoPayMessageBody()}
        <Form id="autopay-selection">{generateFormButtons()}</Form>
        {renderAutoPayFooter()}
      </div>
    </>
  );
};

export default AutopayEnrollmentForm;
